import React, { Fragment, useCallback } from 'react';
import { styled, View } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useNavigation } from '@react-navigation/native';
import { Platform } from 'react-native';

import { getRegExpResults, IS_WEB } from '@utils';
import { BaseText } from '../../common/BaseText';
import { Separator } from '../../common/Separator';
import { AvatarImage } from '../../common/AvatarImage';
import { CollageImage, ICollageImageProps } from './CollageImage';
import { TextTruncated } from '../../common/TextTruncated';
import { RichText } from '../../common/RichText';
import { PostActionPanel } from './PostActionPanel';
import { Post } from '@models/Posts/PostModel';
import { PostActionMenu } from './PostActionMenu';
import type { IPostListStore } from '../PostList';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';
import {
  HomeTabNavigatorNavigationPropType,
  screens,
} from '@navigation';
import { SOUNDCLOUD_URL_REGEX } from '@constants';
import { SoundCloudWidget } from '../../views/SoundCloudWidget';

const Container = styled(View)({});

const AuthorHeader = styled(View)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const TouchableRowContainer = styled(Touchable)({
  flexDirection: 'row',
});

const AuthorFullName = styled(BaseText)({
  variant: IS_WEB ? 'text.medium' : 'text.small',
  fontWeight: 'semi',
  color: '$heading800',
});

const PostTime = styled(BaseText)({
  variant: IS_WEB ? 'text.small' : 'text.xsmall',
  color: '$text80',
});

const Text = styled(RichText)({
  color: '$text400',
  variant: IS_WEB ? 'text.medium' : 'text.small',
});

export interface IPostItemProps {
  item: Post;
  onPress(item: Post, imageIndex?: number): void;
  postListStore: IPostListStore;
}

type NavigationPropType = HomeTabNavigatorNavigationPropType<'News'>;

export const PostItem = observer<IPostItemProps>(
  ({ item, onPress }) => {
    const nav = useNavigation<NavigationPropType>();

    const onPressImage = useCallback<ICollageImageProps['onPress']>(
      (imageIndex) => {
        onPress(item, imageIndex);
      },
      [item, onPress],
    );

    function navigateToUser() {
      if (item.user) {
        nav.push(screens.UserProfileTabNavigator, {
          id: item.user.id,
        });
      }
    }

    const [widgetUrl] = getRegExpResults(
      SOUNDCLOUD_URL_REGEX,
      item.itemData?.text,
    );

    const authorFullName = item.user?.fullName ?? 'Hub';

    const isDisabledUserNavigation = !item.user;

    return (
      <Container>
        <AuthorHeader>
          <TouchableRowContainer
            onPress={navigateToUser}
            // when post is auto-created or created by the admin we can not navigate to his profile because it not exist
            disabled={isDisabledUserNavigation}
            type={Platform.select({
              android: TouchableTypeEnum.WithoutFeedback,
            })}
          >
            {/* If there is no user this means that the post is auto-created
            or created by the admin, in this case, we will show the logo as
            the author's avatar*/}
            {item.user ? (
              <AvatarImage
                source={{ uri: item.user?.photo.small?.url }}
                size={36}
              />
            ) : (
              <AvatarImage
                source={require('../../../../assets/images/logo-icon.png')}
                size={36}
              />
            )}
            <Separator size="$4" vertical />
            <View>
              <AuthorFullName numberOfLines={1}>
                {authorFullName}
              </AuthorFullName>
              <PostTime>
                {item.itemData.formattedPublishDate}
              </PostTime>
            </View>
          </TouchableRowContainer>

          <PostActionMenu item={item} />
        </AuthorHeader>

        {!!item.itemData.text && (
          <Fragment>
            <Separator size={['$5', '$4']} />
            <TextTruncated numberOfLines={3}>
              <Text>{item.itemData.text}</Text>
            </TextTruncated>

            {IS_WEB && !!widgetUrl && (
              <Fragment>
                <Separator size={['$5', '$4']} />
                <SoundCloudWidget
                  width="100%"
                  height="166"
                  visual
                  url={widgetUrl}
                />
              </Fragment>
            )}
          </Fragment>
        )}

        {item.itemData.hasImages && (
          <Fragment>
            <Separator size={['$5', '$4']} />
            <CollageImage
              onPress={onPressImage}
              sources={item.itemData.imagesListPreview}
              isAvailableForCurrentPlan={
                item.isAvailableForCurrentPlan
              }
            />
          </Fragment>
        )}

        {/*  TODO: uncomment this code if you need to enable video support */}
        {/* {item.itemData.videoSource && (
          <Fragment>
            <Separator size={['$5', '$4']} />
            <PostPlayer item={item} postListStore={postListStore} />
          </Fragment>
        )} */}

        <Separator size={['$6', '$8']} />

        <PostActionPanel item={item} />
      </Container>
    );
  },
);
